import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectUser } from '../../entities/session/model/slice';

import {
    closeOrganizationSwitcher,
    selectIsOrganizationChosen,
    selectIsSwitching,
    selectOrganization,
    selectOrganizationId,
} from '../../entities/organization/model/slice';

import {
    useGetOrganizationsAdminQuery,
    useGetOrganizationsQuery,
} from '../../entities/organization/api/organizationApi';

import { baseApi } from '../../shared/api/baseApi';
import { isAdmin } from '../../shared/lib/access';
import { cx } from '../../utils/cx';

import styles from './SelectOrganizationModal.module.scss';
import { IconClose } from '../../assets/icons/icons';

const SEARCH_DEBOUNCE_MS = 350;

export function SelectOrganizationModal() {
    const dispatch = useAppDispatch();

    const user = useAppSelector(selectUser);
    const isChosen = useAppSelector(selectIsOrganizationChosen);
    const isSwitching = useAppSelector(selectIsSwitching);
    const currentId = useAppSelector(selectOrganizationId);

    const [term, setTerm] = useState('');
    const [query, setQuery] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setQuery(term.trim());
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [term]);

    const admin = isAdmin(user);

    const isNeeded = Boolean(user) && (!isChosen || isSwitching);

    const canClose = isChosen;

    const userQuery = useGetOrganizationsQuery(
        { query },
        {
            skip: !isNeeded || admin,
        },
    );

    const adminQuery = useGetOrganizationsAdminQuery(
        { query },
        {
            skip: !isNeeded || !admin,
        },
    );

    const {
        data: organizations = [],
        isLoading,
        isFetching,
    } = admin ? adminQuery : userQuery;

    /* У обычного пользователя ручка организаций может ничего не вернуть —
       например, сразу после входа под ним из «Доступов». Пустое окно
       показывать нечего: организация приходит вместе с сессией. */
    const nothingToPick = !admin && !isLoading && organizations.length === 0;

    const isOpen = isNeeded && !isLoading && !nothingToPick;

    const close = () => {
        if (canClose) {
            dispatch(closeOrganizationSwitcher());
        }
    };

    const choose = (id: string) => {
        if (id === currentId) {
            dispatch(closeOrganizationSwitcher());
            return;
        }

        dispatch(selectOrganization(id));
        dispatch(baseApi.util.resetApiState());
    };

    useEffect(() => {
        if (admin || isChosen || isLoading || isFetching) {
            return;
        }

        /* Одна организация — выбирать не из чего, подставляем молча. */
        if (organizations.length === 1) {
            dispatch(selectOrganization(organizations[0].id));
            return;
        }

        if (organizations.length === 0) {
            dispatch(closeOrganizationSwitcher());
        }
    }, [admin, isChosen, isLoading, isFetching, organizations, dispatch]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close();
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen, canClose]);

    if (!isOpen) {
        return null;
    }

    return createPortal(
        <div
            className={styles.overlay}
            role="dialog"
            aria-modal="true"
            aria-labelledby="org-modal-title"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    close();
                }
            }}
        >
            <div className={styles.window}>
                {canClose && (
                    <button
                        type="button"
                        className={styles.close}
                        onClick={close}
                        aria-label="Закрыть"
                    >
                        <IconClose />
                    </button>
                )}

                <div className={styles.head}>
                    <h2 id="org-modal-title" className={styles.title}>
                        Выберите организацию
                    </h2>

                    <p className={styles.subtitle}>
                        Организацию можно поменять в настройках в любой момент.
                    </p>
                </div>

                <input
                    className={styles.search}
                    type="search"
                    value={term}
                    placeholder="Наименование организации"
                    aria-label="Поиск организации"
                    autoFocus
                    onChange={(event) => setTerm(event.target.value)}
                />

                <div
                    className={cx(
                        styles.list,
                        isFetching && !isLoading && styles.listBusy,
                    )}
                >
                    {isLoading && (
                        <p className={styles.hint}>
                            Загружаем организации…
                        </p>
                    )}

                    {!isLoading && organizations.length === 0 && (
                        <p className={styles.hint}>
                            {query
                                ? 'Ничего не нашлось. Измените запрос.'
                                : 'Доступных организаций нет. Обратитесь в поддержку.'}
                        </p>
                    )}

                    {organizations.map((organization) => (
                        <button
                            key={organization.id}
                            type="button"
                            className={cx(
                                styles.item,
                                organization.id === currentId &&
                                styles.itemCurrent,
                            )}
                            onClick={() => choose(organization.id)}
                        >
                            <span className={styles.itemName}>
                                {organization.name}
                            </span>

                            {organization.inn && (
                                <span className={styles.itemInn}>
                                    ИНН {organization.inn}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {!isLoading && organizations.length > 0 && (
                    <div className={styles.count}>Всего: {organizations.length}</div>
                )}

            </div>
        </div>,
        document.body,
    );
}